import { inject } from '@angular/core';
import { ActivityEntityName, PublicHoliday, PublicHolidayOrigin, manageTenancyRights } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { EntityDataService } from '@workbuddy/services/entities';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { Command } from '@workbuddy/ui/model';
import { ToastService } from '@workbuddy/ui/services';
import type { BulkCommandArgs, EntityBoardBulkCommandsArgs } from '@workbuddy/ui/v2/blocks/entity.board/context';

export const PUBLIC_HOLIDAY_DONT_HONOR = 'public-holiday.dont-honor';
export const PUBLIC_HOLIDAY_HONOR_AGAIN = 'public-holiday.honor-again';
export const PUBLIC_HOLIDAY_DELETE_EXTRA = 'public-holiday.delete';

type HolidayBulkData = {
  run: (rows: PublicHoliday[]) => Promise<void>;
  ask: (message: string, danger?: boolean) => Promise<boolean>;
  message: string;
  danger?: boolean;
};

export function publicHolidayBulkCommands(args: EntityBoardBulkCommandsArgs<PublicHoliday>): Command[] {

  if (!inject(RightsService).hasRights(...manageTenancyRights)) return [];
  const rows = args.selectedItems ?? [];
  if (!rows.length) return [];
  const origins = new Set(rows.map(holidayOrigin));
  if (origins.size !== 1) return [];
  const data = stampHolidayBulkData();
  const origin = [...origins][0];
  if (origin === PublicHolidayOrigin.Library) return [new Command({ _id: PUBLIC_HOLIDAY_DONT_HONOR, label: 'Don\'t honor', iconLeft: 'fa-regular fa-ban', data: { ...data, message: 'Don\'t honor these library days for this year?' } })];
  if (origin === PublicHolidayOrigin.Shadow) return [new Command({ _id: PUBLIC_HOLIDAY_HONOR_AGAIN, label: 'Honor again', iconLeft: 'fa-regular fa-rotate-left', data: { ...data, message: 'Honor these days again?' } })];
  return [new Command({ _id: PUBLIC_HOLIDAY_DELETE_EXTRA, label: 'Delete', iconLeft: 'fa-regular fa-trash', data: { ...data, message: 'Delete these extras?', danger: true } })];
}

export async function handlePublicHolidayBulkCommand(args: BulkCommandArgs<PublicHoliday>): Promise<boolean> {

  const data = args.commandData as HolidayBulkData | undefined;
  if (!data?.run || !data.ask) return false;
  if (!await data.ask(data.message, data.danger)) return true;
  await data.run(args.data ?? []);
  return true;
}

function stampHolidayBulkData(): Pick<HolidayBulkData, 'run' | 'ask'> {

  const confirm = inject(ConfirmServicePro);
  const toasts = inject(ToastService);
  const deleter = inject(EntityDataService).getDelete(ActivityEntityName.PublicHoliday);
  return {
    ask: (message, danger) => confirm.confirm('Public Holidays', message, danger ? 'danger' : 'info'),
    run: async rows => {
      try {
        for (const row of rows) {
          if (!row._id) continue;
          await deleter.mutateAsync({ id: row._id });
        }
        toasts.success('Public Holidays', 'Updated');
      } catch (error) {
        toasts.error('Public Holidays', error instanceof Error ? error.message : 'Unable to update holidays');
        throw error;
      }
    },
  };
}

function holidayOrigin(row: PublicHoliday): PublicHolidayOrigin {

  if (row.origin) return row.origin;
  if (row._id?.startsWith('base:')) return PublicHolidayOrigin.Library;
  return row.kind === 'shadow' ? PublicHolidayOrigin.Shadow : PublicHolidayOrigin.Extra;
}
